import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { dateTime } from '@/lib/format';
import { adminLinks } from '@/lib/links';

export interface EmailRow { id: string; template: string; to_email: string; status: string; created_at: string; subject: string | null; body_text: string | null; person_id?: string | null; batch_id?: string | null }
export const EMAIL_COLUMNS = 'id,template,to_email,status,created_at,subject,body_text,person_id,batch_id';

const TEMPLATE: Record<string, string> = {
  receipt: 'Receipt', renewal_30: 'Renewal reminder (30 days)', renewal_7: 'Last renewal reminder', renewal_dd_30: 'Direct Debit renewal notice',
  expired: 'Membership ended', payment_failed: 'Payment failed', booking_confirmed: 'Booking confirmed',
  grading_signoff: 'Grading sign-off request to instructor', grading_decision: 'Grading decision',
  dbs_soon: 'DBS reminder', dbs_expired: 'DBS run out', insurance_soon: 'Insurance reminder', insurance_expired: 'Insurance run out',
  first_aid_soon: 'First-aid reminder', first_aid_expired: 'First aid run out', declaration_soon: 'Declaration reminder', declaration_expired: 'Declaration run out',
  admin: 'Sent by an admin', instructor: 'Sent by an instructor', refund: 'Refund',
};
const STATUS: Record<string, string> = { queued: 'bg-sky-100 text-sky-800', failed: 'bg-brand-100 text-brand-800', bounced: 'bg-brand-100 text-brand-800' };

/** A list of emails sent, each opening to show exactly what was sent. */
export function EmailsSent({ rows, empty }: { rows: EmailRow[]; empty: string }) {
  if (!rows.length) return <p className="text-sm text-ink-500">{empty}</p>;
  return (
    <ul className="divide-y divide-stone-100 text-sm">{rows.map(e => {
      const kind = TEMPLATE[e.template] ?? e.template.replace(/_/g, ' ');
      return (
        <li key={e.id} className="py-2">
          <details className="group">
            <summary className="flex cursor-pointer list-none flex-wrap items-baseline justify-between gap-x-4 gap-y-1 [&::-webkit-details-marker]:hidden">
              <span className="min-w-0">
                <span className="font-medium">{e.subject ?? kind}</span>
                <span className="block text-ink-500">{e.subject ? `${kind} · ` : ''}to {e.to_email}</span>
              </span>
              <span className="flex items-center gap-2 text-ink-500">{e.status !== 'sent' && <span className={`pill capitalize ${STATUS[e.status] ?? 'bg-stone-100 text-ink-700'}`}>{e.status}</span>}{dateTime(e.created_at)}
                <ChevronDown size={16} className="self-center transition-transform group-open:rotate-180" aria-label="Show email" /></span>
            </summary>
            {e.body_text
              ? <pre className="mt-2 whitespace-pre-wrap border-l-2 border-stone-200 pl-3 font-sans text-ink-700">{e.body_text}</pre>
              : <p className="mt-2 border-l-2 border-stone-200 pl-3 text-ink-500">This email was sent before the portal kept a copy, so its text isn't available.</p>}
            {e.batch_id && <p className="mt-1 pl-3 text-xs"><Link to={adminLinks.emailBatch(e.batch_id)} className="link">See everyone this went to</Link></p>}
          </details>
        </li>
      );
    })}</ul>
  );
}
